import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import { ImageUpload } from './FormComponents';
import OptionGroupsManager, { createPizzaGroups } from './OptionGroupsManager';
import {
  getNome,
  toNum,
  formatBRLDisplay,
  parseBRLInput,
  MENU_CHANNELS,
  CHANNEL_DELIVERY,
  normalizeCanais,
  getChannelConfig,
  buildChannelsWithConfig,
  getChannelPriceOverrideInput,
} from '../utils/dashboardHelpers';

const normalizeIds = (arr) => (Array.isArray(arr) ? arr.map((id) => Number(id)) : []);

const DashboardFormFields = ({ formState, setFormState, cardapios, allCategorias, allComplementos }) => {
  const { type, data, parent } = formState;
  if (!type) return null;
  if (type === 'cardapio') {
    return (
      <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField
        label="Nome do cardápio"
        value={data.nome || ''}
        onChange={(e) => setFormState((s) => ({ ...s, data: { ...s.data, nome: e.target.value } }))}
        fullWidth
        required
      />
      {/* <FormControlLabel
        control={
          <Switch
            checked={!!data.aceita_pedido}
            onChange={(e) => setFormState((s) => ({ ...s, data: { ...s.data, aceita_pedido: e.target.checked } }))}
            />
          }
          label="Aceitar pedidos neste cardápio"
        /> */}
        <FormControlLabel
          control={
            <Switch
              checked={!!data.ativo}
              onChange={(e) => setFormState((s) => ({ ...s, data: { ...s.data, ativo: e.target.checked } }))}
            />
          }
          label="Ativo"
        />
        <Divider />
        <Typography variant="subtitle1" fontWeight={700}>Canais</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {MENU_CHANNELS.map((channel) => (
            <FormControlLabel
              key={channel.code}
              control={
                <Switch
                  checked={getChannelConfig(data, channel.code).ativo !== false}
                  onChange={(e) =>
                    setFormState((s) => {
                      const canais = normalizeCanais(s.data.canais, s.data.ativo !== false);
                      return {
                        ...s,
                        data: {
                          ...s.data,
                          canais: {
                            ...canais,
                            [channel.code]: {
                              ...(canais[channel.code] || {}),
                              ativo: e.target.checked,
                            },
                          },
                        },
                      };
                    })
                  }
                />
              }
              label={channel.label}
            />
          ))}
        </Stack>
      </Stack>
    );
  }
  if (type === 'categoria') {
    return (
      <Stack spacing={2} sx={{ mt: 1 }}>
        <FormControl fullWidth>
          <InputLabel id="cardapio-select-inline">Cardápio</InputLabel>
          <Select
            labelId="cardapio-select-inline"
            label="Cardápio"
            value={data.cardapio_id || parent.cardapio_id || ''}
            onChange={(e) => setFormState((s) => ({ ...s, data: { ...s.data, cardapio_id: e.target.value } }))}
          >
            {cardapios.map((c) => (
              <MenuItem key={c.id} value={c.id}>{getNome(c) || `Cardápio ${c.id}`}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Nome da categoria"
          value={data.nome || ''}
          onChange={(e) => setFormState((s) => ({ ...s, data: { ...s.data, nome: e.target.value } }))}
          fullWidth
          required
        />
        <FormControlLabel
          control={
            <Switch
              checked={!!data.ativo}
              onChange={(e) => setFormState((s) => ({ ...s, data: { ...s.data, ativo: e.target.checked } }))}
            />
          }
          label="Ativa"
        />
      </Stack>
    );
  }
  if (type === 'item') {
    return (
      <Stack spacing={2} sx={{ mt: 1 }}>
        <FormControl fullWidth>
          <InputLabel>Tipo do item</InputLabel>
          <Select
            label="Tipo do item"
            value={data.tipo || 'DEFAULT'}
            onChange={(event) => {
              const nextType = event.target.value;
              setFormState((state) => ({
                ...state,
                data: {
                  ...state.data,
                  tipo: nextType,
                  grupos_opcoes: nextType === 'PIZZA' && !(state.data.grupos_opcoes || []).length
                    ? createPizzaGroups()
                    : (state.data.grupos_opcoes || []),
                },
              }));
            }}
          >
            <MenuItem value="DEFAULT">Item comum</MenuItem>
            <MenuItem value="PIZZA">Pizza</MenuItem>
            <MenuItem value="COMBO_V2">Combo</MenuItem>
          </Select>
        </FormControl>
        <ImageUpload
          image={data.foto_url || ''}
          label="Foto do item"
          recommendedSize="800 × 800 px (formato quadrado)"
          outputWidth={800}
          outputHeight={800}
          onImageAdjusted={(adjustedImage) =>
            setFormState((s) => ({ ...s, data: { ...s.data, foto_url: adjustedImage } }))
          }
        />
        <FormControl fullWidth>
          <InputLabel id="categoria-multi-inline">Categorias</InputLabel>
          <Select
            labelId="categoria-multi-inline"
            label="Categorias"
            multiple
            value={normalizeIds(data.categoria_ids && data.categoria_ids.length ? data.categoria_ids : (data.categoria_id ? [data.categoria_id] : parent.categoria_id ? [parent.categoria_id] : []))}
            onChange={(e) => {
              const ids = normalizeIds(e.target.value);
              setFormState((s) => ({
                ...s,
                data: { ...s.data, categoria_ids: ids, categoria_id: ids?.[0] || '' },
              }));
            }}
            renderValue={(selected) => {
              const cats = allCategorias.filter((c) => selected.includes(Number(c.id)));
              return cats.map((c) => c.cardapio_nome ? `${c.cardapio_nome} - ${getNome(c)}` : getNome(c)).join(', ');
            }}
          >
            {allCategorias.map((c) => (
              <MenuItem key={c.id} value={Number(c.id)}>
                {c.cardapio_nome ? `${c.cardapio_nome} - ${getNome(c)}` : getNome(c)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Nome do item"
          value={data.nome || ''}
          onChange={(e) => setFormState((s) => ({ ...s, data: { ...s.data, nome: e.target.value } }))}
          fullWidth
          required
        />
        <TextField
          label="Descrição"
          value={data.descricao || ''}
          onChange={(e) => setFormState((s) => ({ ...s, data: { ...s.data, descricao: e.target.value } }))}
          fullWidth
          multiline
          minRows={2}
        />
        <TextField
          label="Preço base (salão)"
          value={data.precoInput ?? ''}
          onChange={(e) => {
            const { display, numeric } = parseBRLInput(e.target.value);
            setFormState((s) => ({ ...s, data: { ...s.data, precoInput: display, preco: numeric } }));
          }}
          fullWidth
        />
        {/* <TextField
          label="Código do produto (PDV)"
          value={data.codigo || ''}
          onChange={(e) => setFormState((s) => ({ ...s, data: { ...s.data, codigo: e.target.value } }))}
          fullWidth
        /> */}
        <Divider />
        <Typography variant="subtitle1" fontWeight={700}>Canais</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {MENU_CHANNELS.map((channel) => (
            <FormControlLabel
              key={channel.code}
              control={
                <Switch
                  checked={getChannelConfig(data, channel.code).ativo !== false}
                  onChange={(e) =>
                    setFormState((s) => {
                      const canais = normalizeCanais(s.data.canais, s.data.ativo !== false);
                      return {
                        ...s,
                        data: {
                          ...s.data,
                          canais: {
                            ...canais,
                            [channel.code]: {
                              ...(canais[channel.code] || {}),
                              ativo: e.target.checked,
                            },
                          },
                        },
                      };
                    })
                  }
                />
              }
              label={channel.label}
            />
          ))}
        </Stack>
        {(() => {
          const deliveryCfg = getChannelConfig(data, CHANNEL_DELIVERY);
          const usesBasePrice = deliveryCfg.preco_override == null;
          return (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                p: 1.5,
                backgroundColor: 'rgba(31,79,191,0.04)',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Preço delivery
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Base: {formatBRLDisplay(data.preco || 0)}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={usesBasePrice}
                      onChange={(e) =>
                        setFormState((s) => ({
                          ...s,
                          data: {
                            ...s.data,
                            canais: buildChannelsWithConfig(s.data, CHANNEL_DELIVERY, {
                              preco_override: e.target.checked ? null : toNum(s.data.preco),
                              preco_override_input: e.target.checked ? '' : formatBRLDisplay(s.data.preco),
                            }),
                          },
                        }))
                      }
                    />
                  }
                  label="Usar preço base"
                />
              </Stack>
              {!usesBasePrice && (
                <TextField
                  label="Preço específico delivery"
                  value={getChannelPriceOverrideInput(deliveryCfg)}
                  onChange={(e) => {
                    const { display, numeric } = parseBRLInput(e.target.value);
                    setFormState((s) => ({
                      ...s,
                      data: {
                        ...s.data,
                        canais: buildChannelsWithConfig(s.data, CHANNEL_DELIVERY, {
                          preco_override: numeric,
                          preco_override_input: display,
                        }),
                      },
                    }));
                  }}
                  fullWidth
                  sx={{ mt: 1.25 }}
                />
              )}
            </Box>
          );
        })()}
        <Divider />
        <OptionGroupsManager
          groups={data.grupos_opcoes || []}
          itemType={data.tipo || 'DEFAULT'}
          onChange={(groups) => setFormState((state) => ({
            ...state,
            data: { ...state.data, grupos_opcoes: groups },
          }))}
        />
        {(data.tipo || 'DEFAULT') === 'DEFAULT' && !(data.grupos_opcoes || []).length && (
          <>
        <Divider />
        <Typography variant="subtitle1" fontWeight={700}>Complementos simples já cadastrados</Typography>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={allComplementos}
          value={allComplementos.filter((c) => (data.complemento_ids || []).includes(c.id))}
          onChange={(_, vals) => {
            const ids = vals.map((v) => v.id);
            setFormState((s) => ({
              ...s,
              data: {
                ...s.data,
                complemento_ids: ids,
                complementos: (s.data.complementos || []).filter((c) => ids.includes(c.id)),
              },
            }));
          }}
          getOptionLabel={(option) => option.nome || `Complemento ${option.id}`}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox sx={{ mr: 1 }} checked={selected} />
              {option.nome} - {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(option.preco || 0)}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Selecionar complementos"
              placeholder="Escolha um ou mais complementos"
            />
          )}
        />
        {(data.complemento_ids || []).length === 0 && (
          <Typography variant="body2" color="text.secondary">Cadastre complementos no painel e selecione-os aqui.</Typography>
        )}
        {(data.complemento_ids || []).length > 0 && (
          <Stack spacing={1.25}>
            {(data.complemento_ids || []).map((cid) => {
              const comp = allComplementos.find((c) => c.id === cid) || {};
              const cfg = (data.complementos || []).find((c) => c.id === cid) || {};
              return (
                <Box key={cid} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
                  <Typography fontWeight={700}>{comp.nome || `Complemento ${cid}`}</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="center" mt={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!cfg.obrigatorio}
                          onChange={(e) =>
                            setFormState((s) => ({
                              ...s,
                              data: {
                                ...s.data,
                                complementos: [
                                  ...(s.data.complementos || []).filter((c) => c.id !== cid),
                                  { ...cfg, id: cid, obrigatorio: e.target.checked, min_qtd: e.target.checked ? (cfg.min_qtd || 1) : (cfg.min_qtd || 0) },
                                ],
                              },
                            }))
                          }
                        />
                      }
                      label="Obrigatório"
                    />
                    <TextField
                      label="Mínimo"
                      type="number"
                      value={cfg.min_qtd ?? (cfg.obrigatorio ? 1 : 0)}
                      onChange={(e) =>
                        setFormState((s) => ({
                          ...s,
                          data: {
                            ...s.data,
                            complementos: [
                              ...(s.data.complementos || []).filter((c) => c.id !== cid),
                              { ...cfg, id: cid, min_qtd: parseInt(e.target.value, 10) || 0 },
                            ],
                          },
                        }))
                      }
                      sx={{ width: 130 }}
                      inputProps={{ min: 0 }}
                    />
                    <TextField
                      label="Máx (opcional)"
                      type="number"
                      value={cfg.max_qtd ?? ''}
                      onChange={(e) =>
                        setFormState((s) => ({
                          ...s,
                          data: {
                            ...s.data,
                            complementos: [
                              ...(s.data.complementos || []).filter((c) => c.id !== cid),
                              { ...cfg, id: cid, max_qtd: e.target.value ? parseInt(e.target.value, 10) || null : null },
                            ],
                          },
                        }))
                      }
                      sx={{ width: 130 }}
                      inputProps={{ min: 1 }}
                    />
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
          </>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={!!data.ativo}
              onChange={(e) => setFormState((s) => ({ ...s, data: { ...s.data, ativo: e.target.checked } }))}
            />
          }
          label="Ativo"
        />
      </Stack>
    );
  }
  return null;
};

export default DashboardFormFields;
